import React from 'react';

import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popover, PopoverContent, PopoverPortal, PopoverTrigger } from '@radix-ui/react-popover';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { reservationMessages } from 'src/features/reservations/utils/messages';
import { Button } from 'src/registry/ui/Button';
import { Calendar } from 'src/registry/ui/Calendar';
import { DialogTitle } from 'src/registry/ui/Dialog';
import { FormField, FormItem, FormLabel } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { Separator } from 'src/registry/ui/Separator';
import { generalMessages } from 'src/utils/messages';

type Props = {
    selectedStartDate: Date | undefined;
    setSelectedStartDate: (date: Date | undefined) => void;
    selectedEndDate: Date | undefined;
    setSelectedEndDate: (date: Date | undefined) => void;
};

const ReservationDurationDetails = ({
    selectedStartDate,
    setSelectedStartDate,
    selectedEndDate,
    setSelectedEndDate,
}: Props) => {
    const { control, setValue } = useFormContext();
    const { formatMessage, formatDate } = useIntl();
    const [openStartCalendar, setOpenStartCalendar] = React.useState<boolean>(false);
    const [openEndCalendar, setOpenEndCalendar] = React.useState<boolean>(false);

    const selectedDateToDisplay = (date?: Date) => {
        return date ? formatDate(date) : formatMessage(generalMessages.pickADate);
    };

    return (
        <>
            <DialogTitle>{formatMessage(reservationMessages.stayDuration)}</DialogTitle>
            <Separator className="mb-4" />
            <FormField
                control={control}
                name="fixed_duration_start"
                render={() => {
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                {formatMessage(reservationMessages.reservationDurationStartForm)}
                            </FormLabel>
                            <Popover open={openStartCalendar} onOpenChange={setOpenStartCalendar}>
                                <PopoverTrigger asChild>
                                    <Button
                                        id="date"
                                        variant="outline"
                                        className="text-base font-normal"
                                        onClick={() => setOpenStartCalendar(true)}
                                    >
                                        <FontAwesomeIcon icon={faCalendar} className="text-primary-700 mr-1 size-4" />
                                        {selectedDateToDisplay(selectedStartDate)}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverPortal>
                                    <PopoverContent className="z-50 bg-white p-4 shadow-sm" align="end">
                                        <Calendar
                                            mode="single"
                                            selected={selectedStartDate}
                                            onSelect={(date) => {
                                                if (date) {
                                                    setSelectedStartDate(date);
                                                    setValue('fixed_duration_start', date, {
                                                        shouldValidate: true,
                                                        shouldDirty: true,
                                                    });
                                                    setOpenStartCalendar(!openStartCalendar);
                                                }
                                            }}
                                            disabled={{ before: new Date() }}
                                        />
                                    </PopoverContent>
                                </PopoverPortal>
                            </Popover>
                            <Input
                                type="time"
                                className="justify-center p-2 text-sm"
                                defaultValue={
                                    selectedStartDate
                                        ? `${selectedStartDate.getHours().toString().padStart(2, '0')}:${selectedStartDate.getMinutes().toString().padStart(2, '0')}`
                                        : undefined
                                }
                                onChange={(e) => {
                                    const [hours, minutes] = e.target.value.split(':').map(Number);

                                    if (selectedStartDate) {
                                        const newStartDate = new Date(selectedStartDate);
                                        newStartDate.setHours(hours, minutes);
                                        setSelectedStartDate(newStartDate);
                                        setValue('fixed_duration_start', newStartDate, {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                        });
                                    }
                                }}
                            />
                        </FormItem>
                    );
                }}
            />
            <FormField
                control={control}
                name="fixed_duration_end"
                render={() => {
                    return (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                {formatMessage(reservationMessages.reservationDurationEndForm)}
                            </FormLabel>
                            <Popover open={openEndCalendar} onOpenChange={setOpenEndCalendar}>
                                <PopoverTrigger asChild>
                                    <Button
                                        id="date"
                                        variant="outline"
                                        className="text-base font-normal"
                                        onClick={() => setOpenEndCalendar(true)}
                                    >
                                        <FontAwesomeIcon icon={faCalendar} className="text-primary-700 mr-1 size-4" />
                                        {selectedDateToDisplay(selectedEndDate)}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverPortal>
                                    <PopoverContent className="z-50 bg-white p-4 shadow-sm" align="end">
                                        <Calendar
                                            mode="single"
                                            selected={selectedEndDate}
                                            defaultMonth={selectedEndDate}
                                            onSelect={(date) => {
                                                if (date) {
                                                    setSelectedEndDate(date);
                                                    setValue('fixed_duration_end', date, {
                                                        shouldValidate: true,
                                                        shouldDirty: true,
                                                    });
                                                    setOpenEndCalendar(!openEndCalendar);
                                                }
                                            }}
                                            disabled={{ before: new Date() }}
                                        />
                                    </PopoverContent>
                                </PopoverPortal>
                            </Popover>
                            <Input
                                type="time"
                                className="justify-center p-2 text-sm"
                                defaultValue={
                                    selectedEndDate
                                        ? `${selectedEndDate.getHours().toString().padStart(2, '0')}:${selectedEndDate.getMinutes().toString().padStart(2, '0')}`
                                        : undefined
                                }
                                onChange={(e) => {
                                    const [hours, minutes] = e.target.value.split(':').map(Number);

                                    if (selectedEndDate) {
                                        const newEndDate = new Date(selectedEndDate);
                                        newEndDate.setHours(hours, minutes);
                                        setSelectedEndDate(newEndDate);
                                        setValue('fixed_duration_end', newEndDate, {
                                            shouldValidate: true,
                                            shouldDirty: true,
                                        });
                                    }
                                }}
                            />
                        </FormItem>
                    );
                }}
            />
        </>
    );
};

export default ReservationDurationDetails;
